import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, Save, Loader2, Check, X } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DEFAULT_SLOT = { start_time: "06:00", end_time: "22:00", available: true };

const Availability = () => {
  const { user, getAuthHeaders } = useAuth();
  const [slots, setSlots] = useState(
    DAYS.map((_, idx) => ({ day: idx, ...DEFAULT_SLOT }))
  );
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user?.id) fetchAvailability();
  }, [user]);

  const fetchAvailability = async () => {
    try {
      const res = await axios.get(`${API}/availability/${user.id}`, {
        headers: getAuthHeaders(),
        
      });
      if (res.data.slots && res.data.slots.length > 0) {
        const merged = DAYS.map((_, idx) => {
          const existing = res.data.slots.find(s => s.day === idx);
          return existing || { day: idx, ...DEFAULT_SLOT };
        });
        setSlots(merged);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoaded(true);
    }
  };

  const toggleDay = (dayIdx) => {
    setSlots(prev => prev.map(s =>
      s.day === dayIdx ? { ...s, available: !s.available } : s
    ));
  };

  const updateTime = (dayIdx, field, value) => {
    setSlots(prev => prev.map(s =>
      s.day === dayIdx ? { ...s, [field]: value } : s
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/availability`, { slots }, {
        headers: getAuthHeaders(),
        
      });
      toast.success("Availability saved");
    } catch (e) {
      toast.error("Failed to save availability");
    } finally {
      setSaving(false);
    }
  };

  const setAllAvailable = () => {
    setSlots(prev => prev.map(s => ({ ...s, available: true })));
  };

  const setWeekdaysOnly = () => {
    setSlots(prev => prev.map(s => ({ ...s, available: s.day < 5 })));
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#6A1E2E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="availability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">My Availability</h1>
          <p className="text-[#666666]">Set your preferred available hours for each day of the week</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
          data-testid="save-availability-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Availability
        </Button>
      </div>

      {/* Quick presets */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={setAllAvailable} className="border-[#E5E5E5] text-sm" data-testid="preset-all">
          All Days Available
        </Button>
        <Button variant="outline" size="sm" onClick={setWeekdaysOnly} className="border-[#E5E5E5] text-sm" data-testid="preset-weekdays">
          Weekdays Only
        </Button>
      </div>

      <Card className="border border-[#E5E5E5] shadow-none">
        <CardContent className="pt-6">
          <div className="space-y-1">
            {/* Header */}
            <div className="grid grid-cols-12 gap-3 px-4 pb-3 border-b border-[#E5E5E5]">
              <div className="col-span-3 text-xs font-semibold uppercase tracking-wider text-[#666666]">Day</div>
              <div className="col-span-2 text-xs font-semibold uppercase tracking-wider text-[#666666]">Status</div>
              <div className="col-span-3 text-xs font-semibold uppercase tracking-wider text-[#666666]">From</div>
              <div className="col-span-3 text-xs font-semibold uppercase tracking-wider text-[#666666]">To</div>
              <div className="col-span-1"></div>
            </div>

            {slots.map((slot) => (
              <div
                key={slot.day}
                className={`grid grid-cols-12 gap-3 px-4 py-3 items-center rounded-sm transition-colors ${
                  slot.available ? "bg-white" : "bg-[#F5F5F5]"
                }`}
                data-testid={`availability-day-${slot.day}`}
              >
                <div className="col-span-3">
                  <span className={`font-medium ${slot.available ? "text-[#1A1A1A]" : "text-[#999999]"}`}>
                    {DAYS[slot.day]}
                  </span>
                </div>
                <div className="col-span-2">
                  <button
                    onClick={() => toggleDay(slot.day)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors ${
                      slot.available
                        ? "bg-green-100 text-green-800 hover:bg-green-200"
                        : "bg-red-100 text-red-700 hover:bg-red-200"
                    }`}
                    data-testid={`toggle-day-${slot.day}`}
                  >
                    {slot.available ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    {slot.available ? "Available" : "Unavailable"}
                  </button>
                </div>
                <div className="col-span-3">
                  <Input
                    type="time"
                    value={slot.start_time}
                    onChange={e => updateTime(slot.day, "start_time", e.target.value)}
                    disabled={!slot.available}
                    className={`border-[#E5E5E5] font-mono text-sm h-9 ${!slot.available ? "opacity-40" : ""}`}
                    data-testid={`start-time-${slot.day}`}
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    type="time"
                    value={slot.end_time}
                    onChange={e => updateTime(slot.day, "end_time", e.target.value)}
                    disabled={!slot.available}
                    className={`border-[#E5E5E5] font-mono text-sm h-9 ${!slot.available ? "opacity-40" : ""}`}
                    data-testid={`end-time-${slot.day}`}
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <Clock className={`w-4 h-4 ${slot.available ? "text-[#6A1E2E]" : "text-[#CCCCCC]"}`} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Availability;
